import { inject } from "@angular/core";
import {
	ActivatedRouteSnapshot,
	CanActivateFn,
	Router,
	RouterStateSnapshot,
} from "@angular/router";
import { PermissionsService } from "../services/permissions.service";
import { Auth } from "@angular/fire/auth";
import { UserRole } from "../models/permissions.model";
import { MatSnackBar } from "@angular/material/snack-bar";

export const rolesGuard: CanActivateFn = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
): Promise<boolean> => {
	const permissionsService = inject(PermissionsService);
	const auth = inject(Auth);
	const router = inject(Router);
	const snackBar = inject(MatSnackBar);

	const currentUser = auth.currentUser;
	if (!currentUser) {
		void router.navigate(["/auth/login"]);
		return false;
	}

	const userId = currentUser.uid;
	const requiredRoles = route.data["roles"] as UserRole[];
	const context = route.data["permissionContext"] as "global" | "organization" | "organizations";

	if (!requiredRoles || !context) {
		console.error("Route is missing required roles or permission context");
		snackBar.open("Invalid route configuration", "Close", { duration: 5000 });
		return false;
	}

	// Check if user is global admin first - they have access to everything
	const isGlobalAdmin = await permissionsService.isGlobalAdmin(userId);
	if (isGlobalAdmin) {
		return true;
	}

	switch (context) {
		case "global": {
			const globalPermissions = await permissionsService.getGlobalPermissions(userId);
			const hasGlobalAccess = !!(
				globalPermissions.hasAccess &&
				globalPermissions.roles.some((role) => requiredRoles.includes(role))
			);
			if (!hasGlobalAccess) {
				snackBar.open(permissionsService.getPermissionErrorMessage(context), "Close", {
					duration: 5000,
				});
			}
			return hasGlobalAccess;
		}

		case "organizations": {
			// Check if user has access to any organization with required roles
			const allOrgPermissions = await permissionsService.getAllOrganizationAccess(userId);

			// Check if user has any of the required roles in any organization
			const hasAnyOrgAccess = allOrgPermissions.some(
				(perm) => perm.hasAccess && perm.roles.some((role) => requiredRoles.includes(role)),
			);

			if (!hasAnyOrgAccess) {
				snackBar.open(permissionsService.getPermissionErrorMessage(context), "Close", {
					duration: 5000,
				});
			}
			return hasAnyOrgAccess;
		}

		case "organization": {
			const organizationId = route.params["id"];
			if (!organizationId) {
				console.error("Organization ID is required for organization context");
				snackBar.open("Invalid organization ID", "Close", { duration: 5000 });
				return false;
			}

			const orgPermissions = await permissionsService.getOrganizationPermissions(
				organizationId,
				userId,
			);

			const hasOrgAccess =
				orgPermissions.hasAccess &&
				orgPermissions.roles.some((role) => requiredRoles.includes(role));

			if (!hasOrgAccess) {
				snackBar.open(permissionsService.getPermissionErrorMessage(context), "Close", {
					duration: 5000,
				});
			}
			return hasOrgAccess;
		}

		default:
			console.error("Invalid permission context");
			snackBar.open("Invalid permission context", "Close", { duration: 5000 });
			return false;
	}
};
